import React, { useState, useEffect } from 'react';
import { API_PATHS } from '../../../utils/constants';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Typography,
  Box,
  Alert,
  Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import axios from 'axios';
import api from '../../../services/api';
import authService from '../../../services/auth';
import { v4 as uuidv4, parse as uuidParse } from 'uuid';

/**
 * WorkflowStepAssignDialog component for assigning workflow steps to users
 * 
 * @param {Object} props Component props
 * @param {boolean} props.open Whether the dialog is open
 * @param {Function} props.onClose Function to call when the dialog is closed
 * @param {string} props.stepId The ID of the workflow step to assign
 * @param {Object} props.step The workflow step object with details
 * @param {Function} props.onAssignmentComplete Callback function called when assignment is complete
 */
const WorkflowStepAssignDialog = ({ open, onClose, stepId, step, onAssignmentComplete }) => {
  // State for users
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [notes, setNotes] = useState('');
  const [dueDate, setDueDate] = useState(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)); // Default: 1 week from now
  
  // State for loading and errors
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Get current user's organization ID
  const [organizationId, setOrganizationId] = useState(null);

  // Fetch current user and organization info when dialog opens
  useEffect(() => {
    if (open) {
      fetchCurrentUserInfo();
    }
  }, [open]);

  // Fetch current user info and users from the same organization
  const fetchCurrentUserInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get the token with detailed logging
      let token = localStorage.getItem('jwt_token');
      console.log('Initial token check from jwt_token for workflow step assignment:', token ? 'Found token' : 'No token found');
      
      // Try fallback token sources if the primary source fails
      if (!token) {
        // Try the access_token key which might be used in some auth implementations
        token = localStorage.getItem('access_token');
        console.log('Fallback token check from access_token:', token ? 'Found token' : 'No token found');
        
        // Try getting token from authInfo structure
        if (!token) {
          try {
            const authInfo = localStorage.getItem('authInfo');
            if (authInfo) {
              const authInfoObj = JSON.parse(authInfo);
              token = authInfoObj.token || authInfoObj.accessToken || authInfoObj.jwt_token;
              console.log('Fallback token check from authInfo:', token ? 'Found token' : 'No token found');
            }
          } catch (authParseError) {
            console.error('Error parsing authInfo for token during workflow step assignment:', authParseError);
          }
        }
      }
      
      // Final check if we have a token from any source
      if (!token) {
        console.error('No authentication token available for workflow step assignment');
        setError('Authentication error. Please log in again.');
        setLoading(false);
        return;
      }
      
      console.log('Fetching users for workflow step assignment');
      
      // Setup headers with token
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      const API_URL = API_PATHS.BASE_URL;
      
      // Determine the correct organization type based on user context
      let orgType = 'PARTNER_ORG'; // Default to partner organization
      let userType = null;

      // Get current user info from localStorage to determine organization type
      try {
        const authInfo = localStorage.getItem('authInfo');
        if (authInfo) {
          const parsedAuth = JSON.parse(authInfo);
          userType = parsedAuth?.principalInfo?.userType || 
                    parsedAuth?.user?.userType || 
                    parsedAuth?.userType;
          
          // Only set to CUSTOMER_ORG if the current user is from a customer organization
          if (userType === 'CUSTOMER') {
            orgType = 'CUSTOMER_ORG';
          }
          
          console.log(`Current user type: ${userType}, using organization type: ${orgType}`);
        } else {
          console.log('No auth info found, defaulting to PARTNER_ORG');
        }
      } catch (authError) {
        console.error('Error parsing auth info for organization type:', authError);
      }
      
      // Step 1: Try to get users by type in the current organization
      try {
        console.log(`Fetching users by type in current organization (${orgType})...`);
        const response = await axios.get(`${API_URL}/v1/user/byTypeInCurrentOrg?type=${orgType}`, { headers });
        
        if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
          console.log(`Found ${response.data.length} users from current organization`);
          setUsers(response.data);
          
          // Set organization ID from the first user if available
          if (response.data[0]?.organisation?.id) {
            setOrganizationId(response.data[0].organisation.id);
          }
          
          setLoading(false);
          return;
        } else {
          console.log('No users found in the current organization, trying fallback methods');
        }
      } catch (apiError) {
        console.warn('Error fetching users by type in current organization:', apiError);
        // Continue with fallback methods
      }
      
      // Step 2: Fallback - Try to get users from organization directly
      try {
        // Get the user's organization ID from localStorage
        const orgId = localStorage.getItem('organization_id');
        
        if (orgId) {
          console.log('Found organization ID in localStorage:', orgId);
          const orgUsersResponse = await axios.get(
            `${API_URL}/v1/organisation/${orgId}/users`, 
            { headers }
          );
          
          if (orgUsersResponse?.data && Array.isArray(orgUsersResponse.data) && orgUsersResponse.data.length > 0) {
            console.log(`Found ${orgUsersResponse.data.length} users in the organization`);
            setUsers(orgUsersResponse.data);
            setOrganizationId(orgId);
            setLoading(false);
            return;
          }
        }
      } catch (orgError) {
        console.warn('Failed to get users by organization:', orgError);
      }
      
      // Step 3: Fallback - Try to get all users by type
      try {
        console.log('Attempting to get all users by type...');
        const usersResponse = await axios.get(
          `${API_URL}/v1/user/byType?type=CUSTOMER_ORG`, 
          { headers }
        );
        
        if (usersResponse?.data && Array.isArray(usersResponse.data) && usersResponse.data.length > 0) {
          console.log(`Found ${usersResponse.data.length} users of type CUSTOMER_ORG`);
          setUsers(usersResponse.data);
          setLoading(false);
          return;
        }
      } catch (typeError) {
        console.warn('Failed to get users by type:', typeError);
      }
      
      // Step 4: Last fallback - Use localStorage values
      const currentUserId = localStorage.getItem('user_id');
      const currentUserName = localStorage.getItem('user_name');
      const userEmail = localStorage.getItem('user_email');
      const organizationId = localStorage.getItem('organization_id');
      const organizationName = localStorage.getItem('organization_name');
      
      if (currentUserId && currentUserName) {
        console.log('Using current user from localStorage as last fallback');
        // Create a real user object from localStorage values
        const currentUser = {
          id: currentUserId,
          name: currentUserName,
          email: userEmail || '',
          organisation: { 
            id: organizationId || 'unknown-org', 
            name: organizationName || 'Your Organization' 
          }
        };
        
        // Set this as our only user
        setUsers([currentUser]);
        
        if (organizationId) {
          setOrganizationId(organizationId);
        }
      } else {
        console.error('No user information found in localStorage');
        setError('User information not found. Please log in again.');
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error in fetchCurrentUserInfo:', error);
      setError('Failed to load users. Please try again.');
      setLoading(false);
    }
  };

  // Handle user selection
  const handleUserChange = (event) => {
    setSelectedUser(event.target.value);
  };

  // Handle notes change
  const handleNotesChange = (event) => {
    setNotes(event.target.value);
  };

  // Handle due date change
  const handleDueDateChange = (newDate) => {
    setDueDate(newDate);
  };

  // Handle self-assignment option
  const handleSelfAssign = async () => {
    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);
      
      // Get current user info from localStorage
      const userId = localStorage.getItem('user_id');
      const userName = localStorage.getItem('user_name') || 'Me';
      
      // Get token from localStorage
      const token = localStorage.getItem('jwt_token');
      if (!token || !userId) {
        console.error('No token or user ID found in localStorage');
        setError('Authentication information not found. Please log in again.');
        setSubmitting(false);
        return;
      }
      
      // Setup headers with token
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      // Use axios directly to ensure proper token handling
      const API_URL = API_PATHS.BASE_URL;
      
      // Prepare assignment data with current user
      const assignmentData = {
        workflowStepId: stepId,
        userId: userId,
        notes: 'Self-assigned',
        dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // Default 1 week from now
      };
      
      console.log('Self-assignment data prepared:', assignmentData);
      
      // Call the workflow-assignments endpoint to self-assign
      // Use the public API endpoint for workflow assignments as mentioned in the requirements
      console.log('Making API request to endpoint:', '/v1/api/public/workflow-assignments');
      
      const response = await axios.post(`${API_URL}/v1/api/public/workflow-assignments`, assignmentData, { headers });
      console.log('Self-assignment API response:', response.data);
      
      // Show success message
      setSuccess('Step assigned to yourself');
      
      // Notify parent component
      if (onAssignmentComplete && typeof onAssignmentComplete === 'function') {
        onAssignmentComplete({ 
          selfAssigned: true,
          userId: userId,
          userName: userName
        });
      }
      
      // Close dialog after a short delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error with self-assignment:', error);
      setError('Failed to self-assign. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle assignment submission
  const handleAssign = async () => {
    if (!selectedUser) {
      setError('Please select a user');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);
      
      console.log('Starting workflow step assignment process...');
      
      // Get token from localStorage
      const token = localStorage.getItem('jwt_token');
      if (!token) {
        console.error('No token found in localStorage');
        setError('Authentication token not found. Please log in again.');
        setSubmitting(false);
        return;
      }
      
      console.log('Token retrieved successfully');
      
      // Setup headers with token
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      // Use axios directly to ensure proper token handling
      const API_URL = API_PATHS.BASE_URL;
      
      // Log the user selection
      console.log('Selected user ID:', selectedUser);
      
      // We're using the workflow assignments public endpoint for workflow steps
      // as specified in the system requirements
      console.log('Attempting to assign workflow step via public workflow-assignments endpoint');
      
      // Prepare assignment data with all relevant details
      const assignmentData = {
        workflowStepId: stepId,
        userId: selectedUser,
        notes: notes || '',
        dueDate: dueDate ? dueDate.toISOString() : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
      };
      
      console.log('Assignment data prepared:', assignmentData);
      
      // Call the workflow-assignments endpoint to assign the step to the selected user
      // This uses the public API endpoint that doesn't require authentication
      console.log('Making API request to endpoint:', '/v1/api/public/workflow-assignments');
      
      // Send the authenticated request to the public workflow assignments endpoint
      const response = await axios.post(`${API_URL}/v1/api/public/workflow-assignments`, assignmentData, { headers });
      console.log('Assignment API response:', response.data);
      
      // Show success message
      const successMessage = 'Workflow step successfully assigned to user.';
      console.log(successMessage);
      setSuccess(successMessage);
      
      // Reset form
      setNotes('');
      
      // Find the selected user's name from our users array
      const selectedUserObj = users.find(user => user.id === selectedUser);
      const userName = selectedUserObj ? 
        (selectedUserObj.name || `${selectedUserObj.firstName || ''} ${selectedUserObj.lastName || ''}`.trim() || selectedUserObj.email) : 
        'Assigned User';
        
      // Call onAssignmentComplete callback if provided
      if (onAssignmentComplete && typeof onAssignmentComplete === 'function') {
        onAssignmentComplete({
          userId: selectedUser,
          userName: userName,
          notes: notes,
          dueDate: dueDate,
          selfAssigned: false
        });
      }
      
      // Close dialog after a short delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error assigning workflow step:', error);
      setError('Failed to assign workflow step. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ 
        sx: { 
          borderRadius: 1,
          p: 1
        } 
      }}
    >
      <DialogTitle sx={{ pb: 1, pt: 1, fontSize: '1rem', fontWeight: 'bold' }}>
        Assign Workflow Step
      </DialogTitle>
      
      <Divider />
      
      <DialogContent sx={{ pt: 2, pb: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, py: 0.5 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2, py: 0.5 }}>
            {success}
          </Alert>
        )}
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            Step: {step?.name || 'Workflow Step'}
          </Typography>
          {step?.description && (
            <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
              {step.description}
            </Typography>
          )}
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 2 }}>
              <InputLabel id="assign-user-label">Assign To</InputLabel>
              <Select
                labelId="assign-user-label"
                value={selectedUser}
                onChange={handleUserChange}
                label="Assign To"
              >
                <MenuItem value="">
                  <em>Select a user</em>
                </MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name || (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              label="Assignment Notes"
              multiline
              rows={2}
              variant="outlined"
              size="small"
              fullWidth
              value={notes}
              onChange={handleNotesChange}
              sx={{ mb: 2 }}
            />
            
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Due Date"
                value={dueDate}
                onChange={handleDueDateChange}
                slotProps={{ 
                  textField: { 
                    size: "small", 
                    fullWidth: true,
                    sx: { mb: 1 }
                  } 
                }}
              />
            </LocalizationProvider>
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{ fontSize: '0.75rem' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSelfAssign}
          variant="contained"
          color="secondary"
          size="small"
          sx={{ fontSize: '0.75rem' }}
          disabled={loading || submitting}
        >
          Assign to Me
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          color="primary"
          size="small"
          sx={{ fontSize: '0.75rem' }}
          disabled={!selectedUser || loading || submitting}
        >
          {submitting ? <CircularProgress size={20} /> : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkflowStepAssignDialog;
